import clsx from 'clsx';
import React, { useMemo, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Column } from 'react-table';
import { toTitleCase } from '../../../../../../_metronic/helpers';
import { api } from '../../../../../modules/api/api';
import { useAuth } from '../../../../../modules/auth';
import Avatar from '../../../../../modules/components/avatar/Avatar';
import Datatable from '../../../../../modules/components/datatable/Datatable';
import Header from '../../../../../modules/components/datatable/Header';
import UnlabeledBulletColumns from '../../../../../modules/components/list/UnlabeledBulletColumns';
import ListLoading from '../../../../../modules/components/loading/ListLoading';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import { isoCountryMap } from '../../../../../modules/helpers/isoCountryMap';
import { DataSiftField, SiService, siteIntelligenceServiceLabel } from '../../../mappings';
import EditableCardContainer from '../card/EditableCardContainer';
import { AddWebContent } from '../form/AddWebContent';
import { ServiceSelector, UsePageDataManagerResult } from '../hooks/usePageDataManager';
import { HistoryModal } from '../modal/HistoryModal';
import SummaryTable, { SummaryRow } from '../table/SummaryTable';
import { getFieldUpdate, SiRequestUser } from '../utils';

interface Sort {
  name: string;
  sort: 1 | -1;
}

interface Props {
  data: any;
  isSiAiNewData: boolean;
  reviewer: SiRequestUser[];
  status: string;
  refetch: () => void;
  isEditable?: boolean;
  isSaving?: boolean;
  onUpdate?: UsePageDataManagerResult['updateField'];
  onSave?: UsePageDataManagerResult['saveService'];
  onReset?: UsePageDataManagerResult['resetService'];
  checkDirty?: UsePageDataManagerResult['isServiceDirty'];
}

export default function WebPolicyDetail({
  data,
  isSiAiNewData,
  reviewer,
  status,
  refetch,
  isEditable,
  isSaving,
  onUpdate,
  onSave,
  onReset,
  checkDirty,
}: Props) {
  const { currentUser, auth, refresh } = useAuth();
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id: `SI_WEB_POLICY_DETAIL.${id}` });
  const { setToast } = useToast();
  const emDash = '—';
  const inputFieldClassName = 'form-control form-control-sm fw-normal text-dark';
  const noPolicyTitleValue = emDash;

  const [showDetail, setShowDetail] = useState(false);
  const defaultShowAddFields = {
    [DataSiftField.COMPANY_NAME]: false,
    [DataSiftField.PHONE]: false,
    [DataSiftField.EMAIL]: false,
    [DataSiftField.ADDRESS]: false,
  };
  const [showAddFields, setShowAddFields] =
    useState<Partial<Record<DataSiftField, boolean>>>(defaultShowAddFields);
  const [isEditing, setIsEditing] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const serviceDataSelector: ServiceSelector = (record) =>
    record?.service === SiService.DATA_SIFT_SERVICE && record?.queue_id === data?.queue_id;
  const serviceDataPaths = ['result.policy'];
  const isDirty = checkDirty && checkDirty(serviceDataSelector, serviceDataPaths);

  const params = useParams();
  const sessionPaging = JSON.parse(sessionStorage.getItem(params['*'])); // get paging from session
  const [paging, setPaging] = useState({
    offset: sessionPaging?.offset ?? 0,
    limit: sessionPaging?.limit ?? 15,
  });

  const [sort, setSort] = useReducer(
    (current: Sort, field: Sort['name']): Sort => {
      if (field === current.name) {
        return { name: field, sort: current.sort === 1 ? -1 : 1 };
      } else {
        return { name: field, sort: 1 };
      }
    },
    {
      name: 'created_at',
      sort: -1,
    }
  );
  const results = data;

  const isoCountryOptions = useMemo(
    () =>
      Object.entries(isoCountryMap).map(([code, label]) => ({
        value: code,
        label: label,
      })),
    [isoCountryMap]
  );

  const toggleAddField = (fieldType: DataSiftField) => {
    setShowAddFields((prev) => ({
      ...prev,
      [fieldType]: !prev[fieldType],
    }));
  };

  const handleSave = async () => {
    await onSave(serviceDataSelector, serviceDataPaths);
    setIsEditing(false);
  };

  const handleReset = () => {
    onReset(serviceDataSelector);
  };

  const renderAddFieldSection = (fieldType: DataSiftField) => {
    return (
      isEditable &&
      showAddFields[fieldType] && (
        <AddWebContent
          type={fieldType}
          show={showAddFields[fieldType]}
          onClose={() => toggleAddField(fieldType)}
          onUpdate={onUpdate}
          serviceSelector={serviceDataSelector}
        />
      )
    );
  };

  const reviewerNames: SiRequestUser[] = reviewer;
  const policyFieldUpdates = getFieldUpdate('result.policy', data?.field_updates);
  const getReviewerEmail = (userId?: string) =>
    reviewerNames?.find((reviewer) => reviewer.user_id === userId)?.email ?? userId;
  const getReviewerNames = (fieldUpdates: { updated_by: string }[]) =>
    Array.from(
      new Set(
        fieldUpdates.map((fieldUpdate) => getReviewerEmail(fieldUpdate.updated_by)).filter(Boolean)
      )
    );

  const policyReviewerNames = getReviewerNames(policyFieldUpdates);
  const hasHistory = [policyFieldUpdates].some((fieldUpdates) => fieldUpdates.length > 0);

  const renderReviewers = (reviewerList: string[]) =>
    (reviewerList.length > 0 && isEditable) ? (
      <div className='d-flex align-items-center gap-1 flex-wrap'>
        {reviewerList.map((reviewerName) => (
          <Avatar key={reviewerName} name={reviewerName} />
        ))}
      </div>
    ) : undefined;

  const renderPolicyView = (values: [string, string | string[]][]) => {
    if (isSiAiNewData) {
      return (
        <div>
          <div className='row g-2'>
            {values.map(([link, titles], index) => {
              const titleText =
                typeof titles === 'string' ? titles : titles.filter(Boolean).join(' | ');
              const normalizedLink = link?.trim();
              const hasLink = /^https?:\/\//i.test(normalizedLink);

              return (
                <div
                  key={index}
                  className="col-6 col-md-6 col-xl-4"
                >
                  {hasLink ? (
                    <a
                      href={normalizedLink}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='d-flex align-items-center border border-gray-200 rounded-3 px-3 py-2 bg-light h-100 text-decoration-none'
                    >
                      <span className="d-flex align-items-center justify-content-center flex-shrink-0 me-2">
                        <i className="bi bi-file-earmark-text-fill text-muted fs-6 lh-1"></i>
                      </span>

                      <span
                        className="fw-bold fs-7 text-gray-800 text-truncate w-100 lh-sm"
                        title={normalizedLink || titleText || noPolicyTitleValue}
                      >
                        {titleText || noPolicyTitleValue}
                      </span>
                    </a>
                  ) : (
                    <div className='d-flex align-items-center border border-gray-200 rounded-3 px-3 py-2 bg-light h-100'>
                      <span className="d-flex align-items-center justify-content-center flex-shrink-0 me-2">
                        <i className="bi bi-file-earmark-text-fill text-muted fs-6 lh-1"></i>
                      </span>

                      <span
                        className="fw-bold fs-7 text-gray-800 text-truncate w-100 lh-sm"
                        title={titleText || noPolicyTitleValue}
                      >
                        {titleText || noPolicyTitleValue}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <UnlabeledBulletColumns
        data={values.map(([link, titles], index) => (
          <div className='vstack' key={index}>
            <div>
              {typeof titles === 'string'
                ? titles
                : titles.map((title, idx, arr) => (
                  <React.Fragment key={idx}>
                    {title}
                    {idx !== arr.length - 1 && <span className='text-gray-400 mx-2'>|</span>}
                  </React.Fragment>
                ))}
            </div>
            <a href={link} target='_blank' rel='noopener noreferrer'>
              {link}
            </a>
          </div>
        ))}
        columnCount={1}
        shouldBreakText
      />
    );
  };

  const dataSection: SummaryRow[] = [
    {
      key: 'Policy',
      field: t('POLICY'),
      reviewer: renderReviewers(policyReviewerNames),
      render: () => {
        const values = Object.entries<string | string[]>(results?.result?.policy ?? {}); // url: titles
        return (
          <div className='vstack gap-4'>
            {values?.length > 0 ? (
              isEditing ? (
                <div className='vstack gap-1'>
                  {values.map(([link, titles], index) => (
                    <div className='d-flex gap-2' key={index}>
                      <i
                        className={clsx(
                          'cursor-pointer fs-1x align-middle text-muted fas fa-trash mt-3'
                        )}
                        title='Remove'
                        onClick={() =>
                          onUpdate(serviceDataSelector, (currentDraft) => {
                            if (values.length === 1) {
                              delete currentDraft.result.policy;
                            } else {
                              delete currentDraft.result.policy[link];
                            }
                          })
                        }
                      ></i>
                      <div className='vstack'>
                        <input
                          type='text'
                          className={inputFieldClassName}
                          value={link}
                          placeholder='http://example.com/policy'
                          onChange={(e) =>
                            onUpdate(serviceDataSelector, (currentDraft) => {
                              delete currentDraft.result.policy[link];
                              currentDraft.result.policy[e.target.value] = titles;
                            })
                          }
                        />
                        <CreatableSelect
                          isMulti
                          placeholder={t('POLICY_TITLES')}
                          noOptionsMessage={() =>
                            intl.formatMessage({ id: 'CLIENTS.SELECTION_DESCRIPTION' })
                          }
                          styles={{
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              borderRadius: 0,
                              boxShadow: 'none',
                            }),
                          }}
                          classNames={{
                            control: (state) =>
                              clsx(
                                'h-100 border-gray-300 rounded-end',
                                state.isFocused ? 'border-gray-400' : ''
                              ),
                          }}
                          value={
                            typeof titles === 'string'
                              ? [{ label: titles, value: titles, __isNew__: false }]
                              : titles.map((title) => ({
                                label: title,
                                value: title,
                                __isNew__: false,
                              }))
                          }
                          onChange={(e) => {
                            onUpdate(serviceDataSelector, (currentDraft) => {
                              currentDraft.result.policy[link] =
                                e.length > 1
                                  ? e
                                    .filter((option) => option.value !== noPolicyTitleValue)
                                    .map((option) => option.value.trim())
                                  : e?.[0]?.value?.trim() ?? noPolicyTitleValue;
                            });
                          }}
                          menuPortalTarget={document.body}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                renderPolicyView(values)
              )
            ) : (
              !isEditing && emDash
            )}

            {renderAddFieldSection(DataSiftField.POLICY)}
          </div>
        );
      },
      action: isEditing && (
        <div className='d-flex' onClick={(e) => e.stopPropagation()}>
          <button
            className='btn btn-flush'
            title='Add'
            onClick={() => toggleAddField(DataSiftField.POLICY)}
            disabled={showAddFields[DataSiftField.POLICY]}
          >
            <i className={clsx('fs-3 align-middle fas fa-plus-square text-primary')}></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <EditableCardContainer
        title={t('TITLE')}
        haveHistory={hasHistory && isEditable}
        onHistory={hasHistory ? () => setShowHistoryModal(true) : undefined}
        headerToolbar={
          <>
            {isEditable &&
              (isEditing ? (
                <button
                  onClick={(e) => {
                    e.currentTarget.blur();
                    if (isDirty) {
                      handleReset();
                    }
                    setIsEditing(false);
                    setShowAddFields(defaultShowAddFields);
                  }}
                  className={clsx('btn btn-sm btn-secondary')}
                  title='Cancel Edit'
                >
                  Cancel
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.currentTarget.blur();
                    setIsEditing(true);
                  }}
                  className={clsx('btn btn-sm btn-primary')}
                  title='Edit'
                >
                  Edit
                </button>
              ))}
          </>
        }
        isEditable={isEditable && isEditing}
        isDirty={isDirty}
        isSaving={isSaving}
        onSave={handleSave}
        onReset={handleReset}
      >
        {isSiAiNewData && !isEditing ? (
          <div className='p-9 vstack gap-9'>
            {(() => {
              const values = Object.entries<string | string[]>(results?.result?.policy ?? {});
              return values.length > 0 ? renderPolicyView(values) : emDash;
            })()}
          </div>
        ) : (
          <div className='p-9 vstack gap-9'>
            <SummaryTable
              rows={dataSection}
              data={results}
              refetch={refetch}
              hideHeader={!isEditing}
              className='m-0'
            />
          </div>
        )}
        <div className='d-flex justify-content-end mb-5'>
          <a href='#summary' className='px-8 d-none print-display-button'>
            <i className="bi bi-caret-up-square-fill fs-3"></i>
          </a>
        </div>
      </EditableCardContainer>

      <HistoryModal
        show={showHistoryModal}
        handleClose={() => setShowHistoryModal(false)}
        data={{ results: { users: reviewer ?? [] } }}
        service_paths={[
          {
            ref_id: data?._id,
            paths: serviceDataPaths,
          },
        ]}
      />
    </>
  );
}
